import type { AppReplicache } from "../replicache/instance";
import { ATTACHMENT_PREFIX, type Attachment } from "../replicache/keys";
import { getStagedBlob, purgeBlob } from "./staging";

// Per-attachment upload progress (0–1).  Lives in module scope so any
// component can observe it without going through Replicache (which would
// trigger a sync on every progress tick).  Listeners are notified on every
// change; absence from the map = no active upload.
type ProgressListener = () => void;
const progress = new Map<string, number>();
const listeners = new Set<ProgressListener>();
const notify = () => listeners.forEach((l) => l());

export function subscribeProgress(listener: ProgressListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getProgress(id: string): number | null {
  return progress.has(id) ? (progress.get(id) ?? 0) : null;
}

// Streams a Blob via XHR so we can observe upload progress.  fetch() works
// fine for the PUT but doesn't surface upload events.
function putWithProgress(
  url: string,
  blob: Blob,
  contentType: string,
  onProgress: (loaded: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) onProgress(e.loaded, e.total);
    });
    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`put ${xhr.status}`));
    });
    xhr.addEventListener("error", () => reject(new Error("network error")));
    xhr.addEventListener("abort", () => reject(new Error("aborted")));
    xhr.send(blob);
  });
}

// Background uploader.  Watches Replicache for pending attachments; for each
// one with a staged blob in IDB, requests a presigned URL, PUTs the blob,
// and fires markAttachmentUploaded once done.
//
// We rely on three signals:
//   1. Replicache subscriptions — fire when attachments are added/removed.
//   2. The browser's "online" event — drains the queue on reconnect.
//   3. A periodic 30s tick — covers the cases where neither of the above
//      fires (e.g. tab woken from background, transient server outage).
//
// Concurrency: one upload at a time per attachment id (the `inFlight` set
// prevents double-uploading from re-entrant subscribe callbacks).  Multiple
// tabs may race, but the server is idempotent (mutator only flips status
// once), so the loser's PUT is wasted but not harmful.

type UploaderHandle = {
  stop: () => void;
};

export function startUploader(
  rep: AppReplicache,
  getAccessToken: () => string | null,
): UploaderHandle {
  const inFlight = new Set<string>();
  const failed = new Map<string, number>(); // id → next retry timestamp

  const tryUpload = async (att: Attachment) => {
    if (inFlight.has(att.id)) return;
    if (att.uploadStatus !== "pending") return;

    const blob = await getStagedBlob(att.id);
    if (!blob) {
      // No staged content (e.g. attachment was created on another device).
      // Nothing the uploader can do here.
      return;
    }
    const access = getAccessToken();
    if (!access) return; // no auth yet

    const nextRetry = failed.get(att.id);
    if (nextRetry && Date.now() < nextRetry) return;

    inFlight.add(att.id);
    try {
      const urlRes = await fetch(`/api/attachments/${att.id}/upload-url`, {
        method: "POST",
        headers: { Authorization: `Bearer ${access}` },
      });
      if (!urlRes.ok) {
        if (urlRes.status === 409) {
          // Server says it's already uploaded — purge our local copy and
          // catch the metadata up.
          await purgeBlob(att.id);
          await rep.mutate.markAttachmentUploaded({
            id: att.id,
            status: "uploaded",
          });
          failed.delete(att.id);
          return;
        }
        throw new Error(`upload-url ${urlRes.status}`);
      }
      const { url } = (await urlRes.json()) as {
        url: string;
        method: string;
      };

      progress.set(att.id, 0);
      notify();
      await putWithProgress(url, blob, att.mimeType, (loaded, total) => {
        progress.set(att.id, total > 0 ? loaded / total : 0);
        notify();
      });

      await rep.mutate.markAttachmentUploaded({
        id: att.id,
        status: "uploaded",
      });
      await purgeBlob(att.id);
      failed.delete(att.id);
      progress.delete(att.id);
      notify();
    } catch (err) {
      // Exponential-ish backoff: 5s, 15s, 60s, capped.
      const prior = failed.get(att.id) ?? 0;
      const delay = prior ? Math.min(60_000, (prior - Date.now()) * 2 || 5_000) : 5_000;
      failed.set(att.id, Date.now() + delay);
      progress.delete(att.id);
      notify();
      console.warn(`[uploader] ${att.id} failed:`, err);
    } finally {
      inFlight.delete(att.id);
    }
  };

  const scanAndUpload = async () => {
    const pending: Attachment[] = await rep.query(async (tx) => {
      const list: Attachment[] = [];
      for await (const value of tx.scan({ prefix: ATTACHMENT_PREFIX }).values()) {
        const a = value as Attachment;
        if (a.uploadStatus === "pending") list.push(a);
      }
      return list;
    });
    for (const a of pending) {
      void tryUpload(a);
    }
  };

  // Subscribe so we react immediately to a new createAttachment mutation.
  const unsub = rep.subscribe(
    async (tx) => {
      const list: Attachment[] = [];
      for await (const value of tx.scan({ prefix: ATTACHMENT_PREFIX }).values()) {
        const a = value as Attachment;
        if (a.uploadStatus === "pending") list.push(a);
      }
      return list;
    },
    {
      onData: (pending: Attachment[]) => {
        for (const a of pending) void tryUpload(a);
      },
    },
  );

  const onOnline = () => void scanAndUpload();
  window.addEventListener("online", onOnline);

  const tickId = window.setInterval(() => void scanAndUpload(), 30_000);

  // Kick the first scan now so a freshly-opened tab picks up any
  // attachments staged in a prior session.
  void scanAndUpload();

  return {
    stop: () => {
      unsub();
      window.removeEventListener("online", onOnline);
      window.clearInterval(tickId);
    },
  };
}
